import { config } from "../config.js";
import { requestBootloaderPort, openPort, closePort } from "../serial/webserial.js";
import {
  parseDfuZip,
  DfuOperation,
  WebSerialDfuTransport,
  type DfuUpdate,
  type ProgressEvent,
} from "../dfu/index.js";

type Phase = "idle" | "connecting" | "connected" | "flashing" | "done" | "error";

const ui = {
  browserWarning: document.getElementById("browser-warning") as HTMLElement,
  action: document.querySelector(".action") as HTMLElement,
  controls: document.getElementById("controls") as HTMLElement,
  btnConnect: document.getElementById("btn-connect") as HTMLButtonElement,
  btnFlash: document.getElementById("btn-flash") as HTMLButtonElement,
  progress: document.getElementById("progress") as HTMLElement,
  progressFill: document.getElementById("progress-fill") as HTMLDivElement,
  progressLabel: document.getElementById("progress-label") as HTMLParagraphElement,
  result: document.getElementById("result") as HTMLElement,
  resultSuccess: document.getElementById("result-success") as HTMLElement,
  resultError: document.getElementById("result-error") as HTMLElement,
  resultErrorMessage: document.getElementById("result-error-message") as HTMLParagraphElement,
};

let port: SerialPort | null = null;

function setPrimary(which: "connect" | "flash"): void {
  ui.btnConnect.classList.toggle("btn-primary", which === "connect");
  ui.btnConnect.classList.toggle("btn-secondary", which !== "connect");
  ui.btnFlash.classList.toggle("btn-primary", which === "flash");
  ui.btnFlash.classList.toggle("btn-secondary", which !== "flash");
}

function setPhase(phase: Phase): void {
  switch (phase) {
    case "idle":
      setPrimary("connect");
      ui.btnConnect.disabled = false;
      ui.btnFlash.disabled = true;
      ui.progress.hidden = true;
      ui.result.hidden = true;
      break;
    case "connecting":
      ui.btnConnect.disabled = true;
      ui.btnFlash.disabled = true;
      break;
    case "connected":
      setPrimary("flash");
      ui.btnConnect.disabled = true;
      ui.btnFlash.disabled = false;
      break;
    case "flashing":
      ui.btnConnect.disabled = true;
      ui.btnFlash.disabled = true;
      ui.progress.hidden = false;
      ui.result.hidden = true;
      break;
    case "done":
      ui.btnConnect.disabled = true;
      ui.btnFlash.disabled = true;
      ui.action.hidden = true;
      ui.progress.hidden = true;
      ui.result.hidden = false;
      ui.resultSuccess.hidden = false;
      ui.resultError.hidden = true;
      break;
    case "error":
      if (port !== null) {
        setPrimary("flash");
        ui.btnConnect.disabled = true;
        ui.btnFlash.disabled = false;
      } else {
        setPrimary("connect");
        ui.btnConnect.disabled = false;
        ui.btnFlash.disabled = true;
      }
      ui.action.hidden = false;
      ui.progress.hidden = true;
      ui.result.hidden = false;
      ui.resultSuccess.hidden = true;
      ui.resultError.hidden = false;
      break;
  }
}

function setProgress(pct: number, label: string): void {
  ui.progressFill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  ui.progressLabel.textContent = label;
}

function showError(message: string): void {
  ui.resultErrorMessage.textContent = message;
  setPhase("error");
}

function isWebSerialSupported(): boolean {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

async function onConnectClick(): Promise<void> {
  setPhase("connecting");
  setProgress(0, "Looking for your CNCjogger Stick...");
  ui.progress.hidden = false;
  try {
    const selected = await requestBootloaderPort();
    if (!selected) {
      ui.progress.hidden = true;
      setPhase("idle");
      return;
    }
    await openPort(selected);
    port = selected;
    ui.progress.hidden = true;
    setPhase("connected");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showError(
      `We couldn't connect to your CNCjogger Stick (${msg}). Unplug it, hold the button while plugging it back in, and click Connect again.`,
    );
  }
}

async function loadFirmware(): Promise<{ updates: DfuUpdate[] }> {
  const res = await fetch(config.firmwareUrl);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const zipBytes = new Uint8Array(await res.arrayBuffer());
  const updates = await parseDfuZip(zipBytes);
  return { updates };
}

async function onFlashClick(): Promise<void> {
  if (!port) {
    showError("Lost the connection to your CNCjogger Stick. Click Connect to try again.");
    return;
  }

  setPhase("flashing");
  setProgress(0, "Preparing the update...");

  let transport: WebSerialDfuTransport | null = null;

  try {
    const { updates } = await loadFirmware();

    setProgress(2, "Talking to your CNCjogger Stick...");
    transport = new WebSerialDfuTransport(port);
    const op = new DfuOperation(updates, transport);

    op.setProgressCallback((e: ProgressEvent) => {
      if (e.phase === "init") {
        setProgress(3, "Sending update package...");
      } else {
        const pct = e.bytesTotal === 0 ? 100 : Math.round((e.bytesSent / e.bytesTotal) * 100);
        setProgress(pct, `Installing ${pct}%`);
      }
    });

    await op.start();

    setProgress(100, "Restarting your CNCjogger Stick...");
    await transport.waitForDisconnect();

    port = null;
    setPhase("done");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showError(
      `The update didn't finish (${msg}). Unplug your CNCjogger Stick, put it back into update mode (hold the button while plugging in), and try again.`,
    );
  } finally {
    if (transport) {
      void transport.close();
    }
    if (port) {
      void closePort(port);
      port = null;
    }
  }
}

function init(): void {
  document.title = config.productName;

  if (!isWebSerialSupported()) {
    ui.browserWarning.hidden = false;
    ui.action.hidden = true;
    return;
  }

  ui.btnConnect.addEventListener("click", () => {
    void onConnectClick();
  });
  ui.btnFlash.addEventListener("click", () => {
    void onFlashClick();
  });

  window.addEventListener("beforeunload", () => {
    if (port) void closePort(port);
  });
}

init();
